import { useEffect, useState } from "react";
import axios from "axios";

import { AdminLayout } from "../components/AdminLayout";
import { getAuthHeader } from "../context/AuthContext";

import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

import {
  Loader2,
  CalendarDays,
  Clock3,
  Users,
  Save,
  Settings2,
} from "lucide-react";

import { toast } from "sonner";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const DAYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

export default function ReservationConfigurationPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    reservation_enabled: true,
    reservation_payment_enabled: false,
    reservation_deposit_amount: 0,
    reservation_deposit_type: "fixed",

    slot_duration_minutes: 60,
    buffer_time_minutes: 15,

    max_guests_per_reservation: 10,
    min_guests_per_reservation: 1,

    auto_confirm_reservations: true,
    allow_customer_notes: true,

    reservation_hours: [],
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);

      const response = await axios.get(
        `${API}/reservation/settings`,
        {
          headers: getAuthHeader(),
        }
      );

      const data = response.data;

      if (
        !data.reservation_hours ||
        data.reservation_hours.length === 0
      ) {
        data.reservation_hours = DAYS.map((day) => ({
          day,
          enabled: true,
          start_time: "10:00",
          end_time: "22:00",
        }));
      }

      setForm((prevForm) => ({
        ...prevForm,
        ...data,
      }));
    } catch (error) {
      toast.error(
        "Failed to load reservation settings"
      );
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    try {
      setSaving(true);

      await axios.put(
        `${API}/reservation/settings`,
        form,
        {
          headers: getAuthHeader(),
        }
      );

      toast.success(
        "Reservation settings updated"
      );
    } catch (error) {
      toast.error(
        "Failed to save reservation settings"
      );
    } finally {
      setSaving(false);
    }
  };

  const updateHour = (
    index,
    key,
    value
  ) => {
    const updated = [
      ...form.reservation_hours,
    ];

    updated[index][key] = value;

    setForm({
      ...form,
      reservation_hours: updated,
    });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-10 flex items-center gap-3">
          <Loader2 className="animate-spin h-5 w-5" />
          Loading reservation settings...
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="min-h-screen bg-slate-50 p-6 md:p-10">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 gap-4">
          <div>
            <div className="flex items-center gap-3">
              <div className="bg-primary/10 p-3 rounded-2xl">
                <Settings2 className="h-6 w-6 text-primary" />
              </div>

              <div>
                <h1 className="text-3xl font-bold text-slate-800">
                  Reservation Configuration
                </h1>

                <p className="text-slate-500 mt-1">
                  Configure reservation settings
                  for your restaurant
                </p>
              </div>
            </div>
          </div>

          <Button
            onClick={saveSettings}
            disabled={saving}
            className="h-11 px-6"
          >
            {saving ? (
              <Loader2 className="animate-spin h-4 w-4 mr-2" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}

            Save Settings
          </Button>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* LEFT */}
          <div className="lg:col-span-2 space-y-6">
            {/* General Settings */}
            <Card className="rounded-3xl border-0 shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <CalendarDays className="h-5 w-5 text-primary" />

                  <h2 className="text-xl font-semibold">
                    General Reservation Settings
                  </h2>
                </div>

                <div className="space-y-6">
                  {/* Enable Reservations */}
                  {/* <div className="flex items-center justify-between border rounded-2xl p-4">
                    <div>
                      <Label className="text-base font-medium">
                        Enable Reservations
                      </Label>

                      <p className="text-sm text-slate-500 mt-1">
                        Allow customers to create
                        reservations
                      </p>
                    </div>

                    <Switch
                      checked={
                        form.reservation_enabled
                      }
                      onCheckedChange={(
                        value
                      ) =>
                        setForm({
                          ...form,
                          reservation_enabled:
                            value,
                        })
                      }
                    />
                  </div> */}

                  {/* Auto Confirm */}
                  <div className="flex items-center justify-between border rounded-2xl p-4">
                    <div>
                      <Label className="text-base font-medium">
                        Auto Confirm Reservations
                      </Label>

                      <p className="text-sm text-slate-500 mt-1">
                        Automatically confirm new
                        reservations
                      </p>
                    </div>

                    <Switch
                      checked={
                        form.auto_confirm_reservations
                      }
                      onCheckedChange={(
                        value
                      ) =>
                        setForm({
                          ...form,
                          auto_confirm_reservations:
                            value,
                        })
                      }
                    />
                  </div>

                  {/* Notes */}
                  <div className="flex items-center justify-between border rounded-2xl p-4">
                    <div>
                      <Label className="text-base font-medium">
                        Allow Customer Notes
                      </Label>

                      <p className="text-sm text-slate-500 mt-1">
                        Customers can add special
                        requests
                      </p>
                    </div>

                    <Switch
                      checked={
                        form.allow_customer_notes
                      }
                      onCheckedChange={(
                        value
                      ) =>
                        setForm({
                          ...form,
                          allow_customer_notes:
                            value,
                        })
                      }
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Reservation Hours */}
            <Card className="rounded-3xl border-0 shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <Clock3 className="h-5 w-5 text-primary" />

                  <h2 className="text-xl font-semibold">
                    Reservation Operating Hours
                  </h2>
                </div>

                <div className="space-y-4">
                  {form.reservation_hours.map(
                    (item, index) => (
                      <div
                        key={item.day}
                        className="grid md:grid-cols-4 gap-4 border rounded-2xl p-4 items-center"
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium capitalize">
                            {item.day}
                          </span>

                          <Switch
                            checked={
                              item.enabled
                            }
                            onCheckedChange={(
                              value
                            ) =>
                              updateHour(
                                index,
                                "enabled",
                                value
                              )
                            }
                          />
                        </div>

                        <Input
                          type="time"
                          value={
                            item.start_time
                          }
                          onChange={(e) =>
                            updateHour(
                              index,
                              "start_time",
                              e.target.value
                            )
                          }
                          disabled={
                            !item.enabled
                          }
                        />

                        <Input
                          type="time"
                          value={
                            item.end_time
                          }
                          onChange={(e) =>
                            updateHour(
                              index,
                              "end_time",
                              e.target.value
                            )
                          }
                          disabled={
                            !item.enabled
                          }
                        />

                        <div className="text-sm text-slate-500">
                          {item.enabled
                            ? "Open"
                            : "Closed"}
                        </div>
                      </div>
                    )
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* RIGHT */}
          <div className="space-y-6">
            {/* Slot Settings */}
            <Card className="rounded-3xl border-0 shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <Clock3 className="h-5 w-5 text-primary" />

                  <h2 className="text-xl font-semibold">
                    Slot Settings
                  </h2>
                </div>

                <div className="space-y-5">
                  <div>
                    <Label>
                      Slot Duration (Minutes)
                    </Label>

                    <Input
                      type="number"
                      min="15"
                      value={
                        form.slot_duration_minutes
                      }
                      onChange={(e) =>
                        setForm({
                          ...form,
                          slot_duration_minutes:
                            parseInt(
                              e.target.value
                            ) || 0,
                        })
                      }
                    />
                  </div>

                  <div>
                    <Label>
                      Buffer Time (Minutes)
                    </Label>

                    <Input
                      type="number"
                      min="0"
                      value={
                        form.buffer_time_minutes
                      }
                      onChange={(e) =>
                        setForm({
                          ...form,
                          buffer_time_minutes:
                            parseInt(
                              e.target.value
                            ) || 0,
                        })
                      }
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Guest Settings */}
            <Card className="rounded-3xl border-0 shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <Users className="h-5 w-5 text-primary" />

                  <h2 className="text-xl font-semibold">
                    Guest Settings
                  </h2>
                </div>

                <div className="space-y-5">
                  <div>
                    <Label>
                      Minimum Guests
                    </Label>

                    <Input
                      type="number"
                      min="1"
                      value={
                        form.min_guests_per_reservation
                      }
                      onChange={(e) =>
                        setForm({
                          ...form,
                          min_guests_per_reservation:
                            parseInt(
                              e.target.value
                            ) || 1,
                        })
                      }
                    />
                  </div>

                  <div>
                    <Label>
                      Maximum Guests
                    </Label>

                    <Input
                      type="number"
                      min="1"
                      value={
                        form.max_guests_per_reservation
                      }
                      onChange={(e) =>
                        setForm({
                          ...form,
                          max_guests_per_reservation:
                            parseInt(
                              e.target.value
                            ) || 1,
                        })
                      }
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Reservation Payment */}
            <Card className="rounded-3xl border-0 shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <Users className="h-5 w-5 text-primary" />

                  <h2 className="text-xl font-semibold">
                    Reservation Payment
                  </h2>
                </div>

                <div className="space-y-5">
                  <div className="flex items-center justify-between border rounded-2xl p-4">
                    <div>
                      <Label className="text-base font-medium">
                        Enable Reservation Payment
                      </Label>

                      <p className="text-sm text-slate-500 mt-1">
                        Require payment when customers book a reservation
                      </p>
                    </div>

                    <Switch
                      checked={
                        form.reservation_payment_enabled
                      }
                      onCheckedChange={(value) =>
                        setForm({
                          ...form,
                          reservation_payment_enabled: value,
                        })
                      }
                    />
                  </div>

                  {form.reservation_payment_enabled && (
                    <div className="space-y-5">
                      <div>
                        <Label>
                          Deposit Amount
                        </Label>

                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={
                            form.reservation_deposit_amount
                          }
                          onChange={(e) =>
                            setForm({
                              ...form,
                              reservation_deposit_amount:
                                parseFloat(e.target.value) || 0,
                            })
                          }
                        />
                      </div>

                      <div>
                        <Label>
                          Deposit Type
                        </Label>

                        <select
                          className="mt-2 block w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                          value={
                            form.reservation_deposit_type
                          }
                          onChange={(e) =>
                            setForm({
                              ...form,
                              reservation_deposit_type:
                                e.target.value,
                            })
                          }
                        >
                          <option value="fixed">
                            Fixed
                          </option>
                          <option value="individual">
                            Individual
                          </option>
                        </select>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Summary */}
            <Card className="rounded-3xl border-0 shadow-sm bg-primary text-white">
              <CardContent className="p-6">
                <h2 className="text-lg font-semibold mb-4">
                  Reservation Summary
                </h2>

                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span>Status</span>

                    <span>
                      {form.reservation_enabled
                        ? "Enabled"
                        : "Disabled"}
                    </span>
                  </div>

                  <div className="flex justify-between">
                    <span>Slot Duration</span>

                    <span>
                      {
                        form.slot_duration_minutes
                      }{" "}
                      mins
                    </span>
                  </div>

                  <div className="flex justify-between">
                    <span>Buffer Time</span>

                    <span>
                      {
                        form.buffer_time_minutes
                      }{" "}
                      mins
                    </span>
                  </div>

                  <div className="flex justify-between">
                    <span>Guest Limit</span>

                    <span>
                      {
                        form.min_guests_per_reservation
                      }
                      -
                      {
                        form.max_guests_per_reservation
                      }
                    </span>
                  </div>

                  <div className="flex justify-between">
                    <span>Confirmation</span>

                    <span>
                      {form.auto_confirm_reservations
                        ? "Auto"
                        : "Manual"}
                    </span>
                  </div>

                  <div className="flex justify-between">
                    <span>Payment Required</span>

                    <span>
                      {form.reservation_payment_enabled
                        ? "Yes"
                        : "No"}
                    </span>
                  </div>

                  {form.reservation_payment_enabled && (
                    <>
                      <div className="flex justify-between">
                        <span>Deposit Amount</span>

                        <span>
                          {form.reservation_deposit_amount}
                        </span>
                      </div>

                      <div className="flex justify-between">
                        <span>Deposit Type</span>

                        <span>
                          {form.reservation_deposit_type === "fixed"
                            ? "Fixed"
                            : "Percentage"}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}